import { defer, Observable, of } from "rxjs";
import * as Sentry from "@sentry/angular";
import { FeedbackInternalOptions, FeedbackModalIntegration } from "@sentry/core";
import { TranslationService } from "@app/kernel/translations/translation.service";
import { inject } from "@angular/core";
import AuthUserModelInterface from "@app/kernel/shared/interfaces/auth-user-model.interface";
import { AuthService } from "@app/kernel/tools/services/auth.service";

let sentryFeedbackFormRef: ReturnType<FeedbackModalIntegration["createDialog"]> | undefined;
let currentSentryUser: AuthUserModelInterface | null | undefined = undefined;
let translationServiceRef: TranslationService | undefined; // Store the service here

/**
 * Builds a fully configured {@link FeedbackInternalOptions} object for the Sentry Feedback widget.
 *
 * The options are dynamically localized using the provided {@link TranslationService}
 * and customized depending on the currently authenticated user.
 *
 * @param user - The current authenticated user or `null`/`undefined` if no user is logged in.
 * @param translationService - The translation service used to resolve localized UI strings.
 * @returns A partially constructed {@link FeedbackInternalOptions} object containing:
 * - Visibility and requirement rules for user input fields.
 * - Localized UI labels, placeholders, and messages.
 * - Non-auto-injected configuration to allow dynamic re-rendering when language or user changes.
 *
 * @example
 * ```ts
 * const options = buildFeedbackOptions(currentUser, translationService);
 * const widget = feedback.createWidget(options);
 * ```
 */
function buildFeedbackOptions(
  user: AuthUserModelInterface | null | undefined,
  translationService: TranslationService
): Partial<FeedbackInternalOptions> {
  const options: Partial<FeedbackInternalOptions> = user
    ? {
        showName: false,
        showEmail: !user.email,
        isEmailRequired: !user.email,
        useSentryUser: {
          name: "username",
          email: "email",
        },
      }
    : { showEmail: true, showName: true, isNameRequired: true, isEmailRequired: true };

  return {
    ...options,
    autoInject: false,
    submitButtonLabel: translationService.instant("SENTRY.FEEDBACK.SUBMIT_BUTTON_LABEL"),
    cancelButtonLabel: translationService.instant("SENTRY.FEEDBACK.CANCEL_BUTTON_LABEL"),
    nameLabel: translationService.instant("SENTRY.FEEDBACK.NAME_LABEL"),
    emailLabel: translationService.instant("SENTRY.FEEDBACK.EMAIL_LABEL"),
    messageLabel: translationService.instant("SENTRY.FEEDBACK.MESSAGE_LABEL"),
    triggerLabel: translationService.instant("SENTRY.FEEDBACK.TRIGGER_LABEL"),
    isRequiredLabel: translationService.instant("SENTRY.FEEDBACK.IS_REQUIRED_LABEL"),
    triggerAriaLabel: translationService.instant("SENTRY.FEEDBACK.TRIGGER_ARIA_LABEL"),
    confirmButtonLabel: translationService.instant("SENTRY.FEEDBACK.CONFIRM_BUTTON_LABEL"),
    addScreenshotButtonLabel: translationService.instant("SENTRY.FEEDBACK.ADD_SCREENSHOT_BUTTON_LABEL"),
    removeScreenshotButtonLabel: translationService.instant("SENTRY.FEEDBACK.REMOVE_SCREENSHOT_BUTTON_LABEL"),
    namePlaceholder: translationService.instant("SENTRY.FEEDBACK.NAME_PLACEHOLDER"),
    emailPlaceholder: translationService.instant("SENTRY.FEEDBACK.EMAIL_PLACEHOLDER"),
    messagePlaceholder: translationService.instant("SENTRY.FEEDBACK.MESSAGE_PLACEHOLDER"),
    formTitle: translationService.instant("SENTRY.FEEDBACK.FORM_TITLE"),
    successMessageText: translationService.instant("SENTRY.FEEDBACK.SUCCESS_MESSAGE_TEXT"),
    hideToolText: translationService.instant("SENTRY.FEEDBACK.HIDE_TOOL_TEXT"),
    highlightToolText: translationService.instant("SENTRY.FEEDBACK.HIGHLIGHT_TOOL_TEXT"),
    removeHighlightText: translationService.instant("SENTRY.FEEDBACK.REMOVE_HIGHLIGHT_TEXT"),
  };
}

/**
 * Initializes and manages the Sentry Feedback widget lifecycle.
 *
 * This initializer:
 * - Checks whether Sentry and the Feedback integration are enabled.
 * - Reactively rebuilds the feedback widget whenever the authenticated user
 *   or active application language changes.
 * - Waits until translations for the `"root"` namespace are fully loaded
 *   before rendering the widget to ensure localized UI labels.
 *
 * @returns An RxJS {@link Observable} that completes immediately after initialization.
 *
 * @example
 * ```ts
 * sentryFeedbackInitializer().subscribe();
 * ```
 *
 * @remarks
 * The widget is dynamically removed and recreated whenever:
 * - The user logs in/out (`authService.user$` changes).
 * - The language changes (`translationService.currentLang` changes).
 */
export function sentryFeedbackInitializer(): Observable<unknown> {
  const authService = inject(AuthService);
  const translationService = inject(TranslationService);

  return defer(() => {
    if (!Sentry.isEnabled()) return of(void 0);

    translationServiceRef = translationService;
    authService.user$.subscribe(user => (currentSentryUser = user));

    return of(void 0);
  });
}

/**
 * Programmatically opens the Sentry Feedback form.
 * Uses the existing global Sentry integration instance.
 */
export async function openSentryFeedback(): Promise<void> {
  const feedback = Sentry.getFeedback();

  if (!feedback || !translationServiceRef) {
    console.warn("Sentry Feedback or Translation Service is not ready.");
    return;
  }

  if (sentryFeedbackFormRef) {
    try {
      sentryFeedbackFormRef.removeFromDom();
    } catch (e) {}
    sentryFeedbackFormRef = undefined;
  }

  const latestOptions = buildFeedbackOptions(currentSentryUser, translationServiceRef);

  try {
    sentryFeedbackFormRef = await feedback.createForm(latestOptions);

    if (sentryFeedbackFormRef) {
      sentryFeedbackFormRef.appendToDom();
      sentryFeedbackFormRef.open();
    }
  } catch (error) {
    console.error("Failed to open Sentry Feedback form:", error);
  }
}
